
		import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "dreamland/jsx-runtime";
export const title = "Why it might be a good idea to pull the brakes on AI";
export const description = "My opinions on the current pickle the world finds itself in.";
export const image = "/blog-images/ai-dep.gif";
export const tags = ["Opinion", "Rant"];
function _createMdxContent(props) {
  const _components = {
    code: "code",
    em: "em",
    h2: "h2",
    img: "img",
    li: "li",
    p: "p",
    strong: "strong",
    ul: "ul",
    ...props.components
  };
  return _jsxs(_Fragment, {
    children: [_jsx(_components.p, {
      children: _jsx(_components.img, {
        src: "/blog-images/ai-dep.gif",
        alt: "The XKCD \"Dependencies\" panel, with what appears to be a jack of sorts wedged in the middle, causing half of the stack to tilt precariously to the left."
      })
    }), "\n", _jsxs(_components.p, {
      children: ["I'd like to preface this by saying that I am by no means qualified or deeply knowledgeable in the topic at hand. If you came here expecting a nuanced and well-researched take on the current pace of AI development, ", _jsxs("u", {
        children: ["it's best you turn back ", _jsx(_components.strong, {
          children: "now"
        })]
      }), ". I'm just a moody teenager with a strong opinion and nowhere to voice it."]
    }), "\n", _jsxs(_components.p, {
      children: ["And if you were wondering, this post was never touched by clanker hands (tool calls? shoggoth tentacles?) ", _jsx("br", {}), "\nWhat you're reading is 100% human-created."]
    }), "\n", _jsx(_components.p, {
      children: "With all of that out of the way, let's get started."
    }), "\n", _jsx(_components.h2, {
      children: "Some background"
    }), "\n", _jsxs(_components.p, {
      children: ["The year is (late) 2025. \"Attention Is All You Need\" was published 8 years ago, OpenAI released ", _jsx(_components.code, {
        children: "gpt-3.5-turbo"
      }), " 3 years ago (despite a year earlier refusing to release GPT-3 due to concerns around misinformation!), and we currently live in an absolute hellscape of an AI race. We're continually getting the new \"best model ever\" on a weekly basis. Billions are being poured into anything with \"AI\" in the pitch deck, and if it all goes south, we get a second Great Depression. Not to mention, everyone's racing to build hyperscale datacenters and GPU farms to train these models and do inference with them, which has definitely had quite a few consequences."]
    }), "\n", _jsxs(_components.p, {
      children: ["And you're telling me we haven't stopped to think for just a minute? Let's just go over a ", _jsx(_components.em, {
        children: "few"
      }), " of the adverse effects:"]
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Layoffs"
        }), ": No, this doesn't mean that LLMs can replace people's jobs. It means that managers ", _jsx(_components.em, {
          children: "think"
        }), " that they can."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Environmental Impact"
        }), ": You've heard this one before, but it's still true. Those aforementioned hyperscalers use lots of electricity, and all of that has to come from somewhere. Let's just say, renewables aren't quite our main power source yet, and the noise made from their cooling systems is an absolute nuisance they are to the towns they're built near."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Everyone forgets how to think for themselves"
        }), ": Studies show that making the machine think for you does in fact make you think less."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Misalignment"
        }), ": If we don't stop and make sure the machine is aligned with human values, we'll end up building Skynet."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Misuse out the wazoo"
        }), ": ...wait. That deserves an entire section of its own!"]
      }), "\n"]
    }), "\n", _jsx(_components.h2, {
      children: "How other people are using AI to make the world a worse place"
    }), "\n", _jsx(_components.p, {
      children: "Alright. First of all, let's pick off the easiest thing: fabricated information. As I mentioned before, OpenAI themselves were worried about the proliferation of fake news when GPT-3 was created (that didn't stop them from releasing it anyways!)."
    }), "\n", _jsx(_components.p, {
      children: "The obvious things are fake news stories, backed up by AI-generated images, video, and audio. But that only scratches the surface. Take, for example, scams. Now, a random fella in Nigeria can fake the voice and face of one of your relatives and demand a ransom. Do you really want that future? Your grandparents– hell, even your parents don't stand a chance!"
    }), "\n", _jsx(_components.p, {
      children: "Now, for something a bit more serious. Various private companies (e.g. Palantir, Flock Safety) are using AI-based solutions to enhance their (rather creepy!) surveillance platforms. It's only a matter of time before we slip into the Watch Dogs timeline, and it probably won't be as cool as the games."
    }), "\n", _jsx(_components.p, {
      children: "Anyway, now that I'm getting a bit tired of writing, let's go over a few common arguments against the slowing of AI development in rapid-fire succession."
    }), "\n", _jsx(_components.h2, {
      children: "\"If we stopped, China would beat us!\""
    }), "\n", _jsx(_components.p, {
      children: "Bullshit. If humanity was able to work out nuclear nonproliferation in the 60s, I can guarantee we can agree to put down the H100s for a few years and work out the ethics of this Pandora's box we've opened and maybe regulate it a bit."
    }), "\n", _jsx(_components.p, {
      children: "Also, the Cold War is over, ya damn boomer."
    }), "\n", _jsx(_components.h2, {
      children: "\"You're just a luddite!\""
    }), "\n", _jsx(_components.p, {
      children: "For crying out loud, I'm not calling for some kind of Butlerian Jihad against all autocorrect. I said I just wanted to take a short break."
    }), "\n", _jsx(_components.h2, {
      children: "\"Regulation stifles innovation!\""
    }), "\n", _jsxs(_components.p, {
      children: ["Yeah, apply the same logic to something like cars. Would it really be a great idea to have every road be an Autobahn with no traffic lights or signs? Hopefully ", _jsx(_components.em, {
        children: "then"
      }), " you can understand why putting regulations on AI is a good idea."]
    }), "\n", _jsx(_components.h2, {
      children: "Conclusion"
    }), "\n", _jsx(_components.p, {
      children: "All in all, I really think we should just take a breather, y'know? Figure out if it's really worthwhile to go all in on the tech, and maybe put some rules and regulations in place to tame the beast we've created."
    }), "\n", _jsxs(_components.p, {
      children: ["But of course, that'll never happen, because number must go up ", _jsx(_components.em, {
        children: "now"
      }), "."]
    })]
  });
}
export function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = props.components || ({});
  return MDXLayout ? _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  }) : _createMdxContent(props);
}


		export default function Page() {
			const {wrapper: MDXLayout} = this.components || ({});
			return (
				MDXLayout 
					? _jsx(MDXLayout, { children: [_createMdxContent(this)], ...this })
					: _createMdxContent(this)
			)
		}
	